import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import client from '../../api/client';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useCompanyStore } from '../../stores/companyStore';
import type { Company } from '../../types/models';
import {
  summaryAllStatuses,
  REVIEW_STATUS_LABELS_HE,
  REVIEW_STATUS_COLORS,
  type CompanyStatusSummary,
} from '../../api/companyStatus';

const ACTIVE_THRESHOLD_DAYS = 365;

function isCompanyActive(c: Company): boolean {
  // Unknown freshness ≠ inactive. We only flag a company as inactive
  // when we actually have a posting date AND it's older than the
  // threshold. Companies with no freshness data yet (e.g. wiz shells
  // before the first cross-audit run) stay visible.
  if (!c.last_journal_date) return true;
  const last = new Date(c.last_journal_date);
  if (Number.isNaN(last.getTime())) return true;
  const ageDays = (Date.now() - last.getTime()) / 86_400_000;
  return ageDays <= ACTIVE_THRESHOLD_DAYS;
}

export default function CompanySelector() {
  const { companies, selectedCompanyId, selectCompany, recentIds } = useCompanyStore();

  // Persist the user's "include inactive" choice across sessions.
  const [includeInactive, setIncludeInactive] = useState<boolean>(() => {
    return localStorage.getItem('hash_company_include_inactive') === '1';
  });
  useEffect(() => {
    localStorage.setItem('hash_company_include_inactive', includeInactive ? '1' : '0');
  }, [includeInactive]);

  const selected = companies.find((c) => c.id === selectedCompanyId) ?? null;
  const [inputValue, setInputValue] = useState(selected?.name ?? '');

  useEffect(() => {
    setInputValue(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  // Filter + order the option list. Recently-selected companies float
  // to the top (in MRU order), everything else sorted by name. Always
  // keeps the currently selected company visible.
  const visibleCompanies = useMemo(() => {
    const base = includeInactive
      ? companies
      : companies.filter((c) => isCompanyActive(c) || c.id === selectedCompanyId);
    const byId = new Map(base.map((c) => [c.id, c]));
    const recents: Company[] = [];
    for (const id of recentIds) {
      const c = byId.get(id);
      if (c) {
        recents.push(c);
        byId.delete(id);
      }
    }
    return [...recents, ...byId.values()];
  }, [companies, includeInactive, selectedCompanyId, recentIds]);

  // True for companies that should be visually grouped under "אחרונים"
  // — i.e. they're in the MRU set AND we're currently viewing the
  // unfiltered list (so the visual section header makes sense).
  const recentIdSet = useMemo(() => new Set(recentIds), [recentIds]);

  // Status pills — one round-trip on mount, keyed by company id.
  const statusQ = useQuery({
    queryKey: ['company-status', 'summary-all'],
    queryFn: () => summaryAllStatuses(),
    staleTime: 30_000,
  });

  // Pre-fetch on hover. The expensive thing about selecting a new
  // company is the dashboard's initial data fetch; warming it while
  // the user is still hovering an option in the autocomplete makes
  // the actual select feel instant.
  const qc = useQueryClient();
  const prefetchCompany = (companyId: string) => {
    // Don't prefetch the currently-selected company.
    if (companyId === selectedCompanyId) return;
    qc.prefetchQuery({
      queryKey: ['risk', 'score', companyId],
      queryFn: async () => {
        const r = await client.get('/risk/risk-score', {
          headers: { 'X-Company-ID': companyId },
        });
        return r.data;
      },
      staleTime: 30_000,
    });
    qc.prefetchQuery({
      queryKey: ['company-status', 'list', companyId],
      queryFn: async () => {
        const r = await client.get('/company-status/review-status', {
          headers: { 'X-Company-ID': companyId },
        });
        return r.data;
      },
      staleTime: 30_000,
    });
  };
  const statusById = useMemo(() => {
    const m = new Map<string, CompanyStatusSummary>();
    for (const s of statusQ.data ?? []) m.set(s.company_id, s);
    return m;
  }, [statusQ.data]);

  if (companies.length === 0) return null;

  const inactiveCount = companies.length - companies.filter(isCompanyActive).length;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Autocomplete
        size="small"
        sx={{ minWidth: 280 }}
        options={visibleCompanies}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        disableClearable
        selectOnFocus
        handleHomeEndKeys
        openOnFocus
        getOptionLabel={(option: Company) => option.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        groupBy={(option: Company) =>
          recentIdSet.has(option.id) ? 'אחרונים' : 'כל החברות'
        }
        onChange={(_, value) => {
          if (value) selectCompany(value.id);
        }}
        renderOption={(props, option) => {
          const active = isCompanyActive(option);
          const dateHint = option.last_journal_date
            ? new Date(option.last_journal_date).toLocaleDateString('he-IL')
            : '';
          const status = statusById.get(option.id);
          const pill = status?.status
            ? REVIEW_STATUS_COLORS[status.status]
            : null;
          return (
            <Box
              component="li"
              {...props}
              key={option.id}
              onMouseEnter={() => prefetchCompany(option.id)}
              onFocus={() => prefetchCompany(option.id)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                opacity: active ? 1 : 0.55,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, flexGrow: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    color: active ? 'text.primary' : 'text.disabled',
                    fontWeight: active ? 500 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.name}
                </Typography>
                {status?.status && pill && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      bgcolor: pill.bg,
                      color: pill.fg,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {REVIEW_STATUS_LABELS_HE[status.status]}
                    {status.year ? ` · ${status.year}` : ''}
                  </Box>
                )}
              </Box>
              <Typography
                component="span"
                sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}
              >
                {dateHint}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="חברה"
            onFocus={(e) => e.target.select()}
          />
        )}
      />
      {inactiveCount > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: 12 }}>
              כולל לא פעילות ({inactiveCount})
            </Typography>
          }
          sx={{ m: 0 }}
        />
      )}
    </Stack>
  );
}
